/* ==========================================================================
   pa-header.js: <pa-header> controller for sections/pa-header.liquid (Red Line header).
   - Desktop menus (Calibers, Brands mega; Help dropdown): 150 ms hover intent,
     click, Enter / Space / ArrowDown; close on Escape (focus back to the trigger),
     outside click, focus leaving the header, or a 200 ms pointer-leave.
   - Mobile drawer: slide in, body scroll lock, focus trap, Escape / close button.
   - Search overlay: drops in, debounced predictive fetch of the
     pa-predictive-search section (predictive-search.js stays frozen), shorthand
     caliber chips, the matching stockpile deal row.
   - Cart count: keeps #cart-icon-bubble's CTA-colour count in step after quick-adds and
     UpCart edits (reads /cart.js; never touches the add-to-cart request itself).
   - <sticky-header>: hide on scroll down, show on scroll up, with the same section classes
     (shopify-section-header-sticky / -hidden) the old header used, so pa-rl-tabs.js,
     collection-range-cans.js and global.js keep reading the same state. Defined here only when
     the theme has not defined it already.
   No dependency on legacy header JS. Guarded custom elements.
   ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('pa-header')) return;

  /* ------------------------------------------------------------ sticky-header */
  if (!customElements.get('sticky-header')) {
    var StickyHeader = class extends HTMLElement {
      connectedCallback() {
        this.header = this.closest('.shopify-section-header') || document.querySelector('.shopify-section-header');
        if (!this.header) return;
        this.headerBounds = {};
        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.scrollUpActive = this.dataset.scrollUp !== undefined;
        this.isAtTop = true;
        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => { this.preventReveal = true; };
        window.addEventListener('scroll', this.onScrollHandler, {passive: true});
        this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        this.createObserver();
      }

      disconnectedCallback() {
        this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.removeEventListener('scroll', this.onScrollHandler);
      }

      createObserver() {
        var self = this;
        var observer = new IntersectionObserver(function (entries) {
          var r = entries[0].boundingClientRect;
          var y = window.pageYOffset || document.documentElement.scrollTop;
          self.headerBounds = {top: y + r.top, bottom: y + r.bottom};
          observer.disconnect();
        });
        observer.observe(this.header);
      }

      busy() {
        return !!this.querySelector('pa-header[data-open]');
      }

      onScroll() {
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        if (this.busy()) {
          this.currentScrollTop = scrollTop;
          return;
        }
        if (scrollTop <= 0) {
          this.header.classList.remove('shopify-section-header-sticky', 'shopify-section-header-hidden', 'animate');
          document.body.classList.remove('shopify-body-section-header-sticky', 'shopify-body-section-header-hidden');
          this.isAtTop = true;
          this.currentScrollTop = scrollTop;
          return;
        }
        if (this.isAtTop && scrollTop > 10) this.isAtTop = false;
        if (this.isAtTop) {
          this.currentScrollTop = scrollTop;
          return;
        }
        if (this.scrollUpActive) {
          if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
            requestAnimationFrame(this.hide.bind(this));
          } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
            if (!this.preventReveal) {
              requestAnimationFrame(this.reveal.bind(this));
            } else {
              var self = this;
              window.clearTimeout(this.isScrolling);
              this.isScrolling = setTimeout(function () { self.preventReveal = false; }, 66);
              requestAnimationFrame(this.hide.bind(this));
            }
          } else if (scrollTop <= this.headerBounds.top) {
            requestAnimationFrame(this.reset.bind(this));
          }
        } else if (scrollTop > this.headerBounds.bottom) {
          if (!this.sticky()) requestAnimationFrame(this.reveal.bind(this));
        } else if (scrollTop <= this.headerBounds.top) {
          requestAnimationFrame(this.reset.bind(this));
        }
        this.currentScrollTop = scrollTop;
      }

      hide() {
        if (this.isAtTop || this.busy()) return;
        this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
        document.body.classList.add('shopify-body-section-header-hidden', 'shopify-body-section-header-sticky');
      }

      reveal() {
        if (this.isAtTop) return;
        this.header.classList.add('shopify-section-header-sticky', 'animate');
        this.header.classList.remove('shopify-section-header-hidden');
        document.body.classList.add('shopify-body-section-header-sticky');
        document.body.classList.remove('shopify-body-section-header-hidden');
      }

      reset() {
        this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
        document.body.classList.remove('shopify-body-section-header-hidden', 'shopify-body-section-header-sticky');
      }

      sticky() {
        return this.header.classList.contains('shopify-section-header-sticky') && !this.header.classList.contains('shopify-section-header-hidden');
      }
    };
    customElements.define('sticky-header', StickyHeader);
  }

  var DESKTOP = '(min-width: 1100px)';
  var HOVER_OPEN_MS = 150;
  var HOVER_CLOSE_MS = 200;
  var PANEL_MS = 200;
  var SHEET_MS = 320;
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function isDesktop() {
    return !!(window.matchMedia && window.matchMedia(DESKTOP).matches);
  }

  function visibleFocusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      if (el.closest('[hidden]')) return false;
      var d = el.closest('details');
      while (d) {
        if (!d.open && !(el.tagName === 'SUMMARY' && el.parentElement === d)) return false;
        d = d.parentElement ? d.parentElement.closest('details') : null;
      }
      var r = el.getBoundingClientRect();
      return r.width > 0 || r.height > 0;
    });
  }

  /* Show/hide with a transition; `hidden` is the resting state so lazy images
     inside closed panels never load early. */
  function reveal(el) {
    if (!el) return;
    clearTimeout(el._paHideTimer);
    el.hidden = false;
    // Force a style flush so the transition runs from the closed state.
    void el.offsetWidth;
    el.classList.add('is-open');
  }

  function conceal(el, ms, done) {
    if (!el) return;
    el.classList.remove('is-open');
    clearTimeout(el._paHideTimer);
    var finish = function () {
      if (!el.classList.contains('is-open')) el.hidden = true;
      if (done) done();
    };
    if (reduced() || el.hidden) finish();
    else el._paHideTimer = setTimeout(finish, ms);
  }

  /* r1 H3: a click or tap (not Enter / Space, which fire click with detail 0 and no pointerType). */
  function byPointer(event) {
    if (!event) return false;
    if (event.pointerType) return true;
    return event.detail > 0;
  }

  /* Move focus without a focus ring after a pointer action. focusVisible:false where the browser
     supports it; the is-quiet-focus class covers the rest and drops on blur or the next key. */
  function focusQuiet(el) {
    if (!el) return;
    el.classList.add('is-quiet-focus');
    var clear = function () {
      el.classList.remove('is-quiet-focus');
      el.removeEventListener('blur', clear);
      el.removeEventListener('keydown', clear);
    };
    el.addEventListener('blur', clear);
    el.addEventListener('keydown', clear);
    try {
      el.focus({preventScroll: true, focusVisible: false});
    } catch (e) {
      el.focus({preventScroll: true});
    }
  }

  /* Input modality on <html>: set by a pointer or touch press, cleared by any key. CSS drops the
     ring on the menu button and the drawer close while it is set, whatever path moved focus. */
  (function () {
    var root = document.documentElement;
    var on = function () { root.setAttribute('data-pa-pointer', ''); };
    var off = function (e) { if (e.key !== 'Shift' && e.key !== 'Meta' && e.key !== 'Control' && e.key !== 'Alt') root.removeAttribute('data-pa-pointer'); };
    document.addEventListener('pointerdown', on, true);
    document.addEventListener('touchstart', on, {capture: true, passive: true});
    document.addEventListener('keydown', off, true);
  })();

  var lockCount = 0;
  function lockScroll(on) {
    lockCount = Math.max(0, lockCount + (on ? 1 : -1));
    var locked = lockCount > 0;
    document.body.classList.toggle('pa-lock', locked);
    document.documentElement.style.overflow = locked ? 'hidden' : '';
    // Modal sheets: the page behind is inert (no focus, no screen-reader travel). On phones the
    // sheet covers it entirely, so it is also hidden from paint (pa-header.css).
    Array.prototype.forEach.call(document.querySelectorAll('#MainContent, .shopify-section-group-footer-group'), function (el) {
      if (locked) el.setAttribute('inert', '');
      else el.removeAttribute('inert');
    });
  }

  class PaHeader extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.scrim = this.querySelector('[data-pa-scrim]');
      this.triggers = Array.prototype.slice.call(this.querySelectorAll('[data-pa-menu]'));
      this.openName = null;
      this.openedBy = null;

      this._onDocClick = this._onDocClick.bind(this);
      this._onKey = this._onKey.bind(this);
      this._onFocusIn = this._onFocusIn.bind(this);
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKey);
      document.addEventListener('focusin', this._onFocusIn);

      this._initMenus();
      this._initDrawer();
      this._initSearch();
      this._initCart();

      var self = this;
      this._mq = window.matchMedia(DESKTOP);
      this._onMq = function () {
        self.closeMenu(false);
        if (self.mq_desktop() && self.drawerOpen) self.closeDrawer(false);
        if (self.searchOpen) self.closeSearch(false);
      };
      if (this._mq.addEventListener) this._mq.addEventListener('change', this._onMq);
      else if (this._mq.addListener) this._mq.addListener(this._onMq);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKey);
      document.removeEventListener('focusin', this._onFocusIn);
      if (this._mq) {
        if (this._mq.removeEventListener) this._mq.removeEventListener('change', this._onMq);
        else if (this._mq.removeListener) this._mq.removeListener(this._onMq);
      }
      if (this._perf) this._perf.disconnect();
      if (this._bubbleObs) this._bubbleObs.disconnect();
      if (this.drawerOpen || this.searchOpen) lockScroll(false);
      this._ready = false;
    }

    mq_desktop() {
      return isDesktop();
    }

    /* data-open while a menu, the drawer or the search is open: the sticky header never hides
       under an open panel. */
    _sync() {
      this.toggleAttribute('data-open', !!(this.openName || this.drawerOpen || this.searchOpen));
    }

    /* ---------------------------------------------------------------- menus */
    _panel(name) {
      return this.querySelector('[data-pa-panel="' + name + '"]');
    }

    _trigger(name) {
      return this.querySelector('[data-pa-menu="' + name + '"]');
    }

    _initMenus() {
      var self = this;
      this.triggers.forEach(function (trigger) {
        var name = trigger.getAttribute('data-pa-menu');
        var panel = self._panel(name);
        if (!panel) return;
        var zones = [trigger, panel];

        trigger.addEventListener('click', function (event) {
          event.preventDefault();
          var keyboard = event.detail === 0;
          if (self.openName === name) {
            if (self.openedBy === 'hover' && !keyboard) {
              self.openedBy = 'click';
              return;
            }
            self.closeMenu(keyboard ? 'key' : false);
          } else {
            self.openMenu(name, keyboard ? 'keyboard' : 'click');
          }
        });

        trigger.addEventListener('keydown', function (event) {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            self.openMenu(name, 'keyboard');
          }
        });

        zones.forEach(function (zone) {
          zone.addEventListener('pointerenter', function (event) {
            if (event.pointerType !== 'mouse' || !isDesktop()) return;
            clearTimeout(self._closeTimer);
            if (zone !== trigger) return;
            clearTimeout(self._openTimer);
            if (self.openName && self.openName !== name) {
              self.openMenu(name, 'hover');
              return;
            }
            if (self.openName === name) return;
            self._openTimer = setTimeout(function () {
              self.openMenu(name, 'hover');
            }, HOVER_OPEN_MS);
          });
          zone.addEventListener('pointerleave', function (event) {
            if (event.pointerType !== 'mouse') return;
            clearTimeout(self._openTimer);
            if (self.openName !== name || self.openedBy === 'keyboard') return;
            clearTimeout(self._closeTimer);
            self._closeTimer = setTimeout(function () {
              self.closeMenu(false);
            }, HOVER_CLOSE_MS);
          });
        });
      });

      if (this.scrim) {
        this.scrim.addEventListener('click', function () {
          if (self.searchOpen) self.closeSearch(false);
          else self.closeMenu(false);
        });
      }
    }

    openMenu(name, by) {
      if (this.searchOpen) this.closeSearch(false);
      var panel = this._panel(name);
      var trigger = this._trigger(name);
      if (!panel || !trigger) return;
      clearTimeout(this._closeTimer);
      clearTimeout(this._openTimer);
      if (this.openName && this.openName !== name) {
        // Switch instantly between menus: no fade-out stacking.
        var prev = this._panel(this.openName);
        var prevTrigger = this._trigger(this.openName);
        if (prevTrigger) prevTrigger.setAttribute('aria-expanded', 'false');
        if (prev) {
          prev.classList.remove('is-open');
          prev.hidden = true;
        }
      }
      this.openName = name;
      this.openedBy = by;
      trigger.setAttribute('aria-expanded', 'true');
      reveal(panel);
      this._sync();
      if (name === 'help') {
        if (this.scrim && !this.scrim.hidden) conceal(this.scrim, PANEL_MS);
      } else {
        this._edgeScrim(panel);
        reveal(this.scrim);
      }
      if (by === 'keyboard') {
        var first = visibleFocusables(panel)[0];
        if (first) first.focus({preventScroll: true});
      }
    }

    /* loop98 r1 F18: the scrim is solid ink for 56 px under the open panel's bottom edge and
       fades to its .86 over the next 64, so no page heading cut by the panel edge reads through
       as a sliver. The panel height is measured on open (it varies by menu). */
    _edgeScrim(panel) {
      if (!this.scrim || !panel) return;
      this.scrim.style.setProperty('--pa-panel-b', panel.offsetHeight + 'px');
    }

    closeMenu(returnFocus) {
      if (!this.openName) return;
      var name = this.openName;
      var panel = this._panel(name);
      var trigger = this._trigger(name);
      /* Header fix 5: Escape returns focus to the trigger with a ring only when the menu was
         opened from the keyboard or focus is inside it. A menu opened by hover or click keeps the
         trigger unringed (focusQuiet) or leaves focus where it was, so CALIBERS shows nothing
         once its menu is closed. returnFocus 'key': closed by Enter / Space on the trigger. */
      var active = document.activeElement;
      var focusInside = !!(panel && active && panel.contains(active));
      var keyboardOpen = returnFocus === 'key' || this.openedBy === 'keyboard' || focusInside;
      var pointerFocus = !keyboardOpen && active === trigger;
      this.openName = null;
      this.openedBy = null;
      clearTimeout(this._openTimer);
      clearTimeout(this._closeTimer);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      conceal(panel, PANEL_MS);
      this._sync();
      if (!this.searchOpen) conceal(this.scrim, PANEL_MS);
      if (!returnFocus || !trigger) return;
      if (keyboardOpen) trigger.focus({preventScroll: true});
      else if (pointerFocus) focusQuiet(trigger);
    }

    /* --------------------------------------------------------------- drawer */
    _initDrawer() {
      var self = this;
      this.drawer = this.querySelector('#pa-nav-drawer');
      this.drawerOpenBtn = this.querySelector('[data-pa-drawer-open]');
      this.drawerOpen = false;
      this.drawerScrim = this.querySelector('[data-pa-drawer-scrim]');
      if (!this.drawer || !this.drawerOpenBtn) return;
      if (this.drawerScrim) this.drawerScrim.addEventListener('click', function () {
        self.closeDrawer(true, true);
      });
      this.drawerOpenBtn.addEventListener('click', function (event) {
        self.openDrawer(byPointer(event));
      });
      var close = this.drawer.querySelector('[data-pa-drawer-close]');
      if (close) close.addEventListener('click', function (event) {
        self.closeDrawer(true, byPointer(event));
      });
      this.drawer.addEventListener('keydown', function (event) {
        if (event.key === 'Tab') self._trap(event, self.drawer);
      });
    }

    openDrawer(quiet) {
      if (!this.drawer || this.drawerOpen) return;
      if (this.searchOpen) this.closeSearch(false);
      this.drawerOpen = true;
      this.drawer.scrollTop = 0;
      this.drawerOpenBtn.setAttribute('aria-expanded', 'true');
      reveal(this.drawerScrim);
      reveal(this.drawer);
      lockScroll(true);
      this._sync();
      var close = this.drawer.querySelector('[data-pa-drawer-close]');
      if (close && quiet) focusQuiet(close);
      else if (close) close.focus({preventScroll: true});
    }

    /* quiet: the close came from a tap or click, so focus returns to the menu button without a
       ring (r1 H3). Escape and keyboard activation keep the visible ring. */
    closeDrawer(returnFocus, quiet) {
      if (!this.drawer || !this.drawerOpen) return;
      this.drawerOpen = false;
      this.drawerOpenBtn.setAttribute('aria-expanded', 'false');
      conceal(this.drawer, SHEET_MS);
      conceal(this.drawerScrim, SHEET_MS);
      lockScroll(false);
      this._sync();
      if (returnFocus && quiet) focusQuiet(this.drawerOpenBtn);
      else if (returnFocus) this.drawerOpenBtn.focus({preventScroll: true});
    }

    _trap(event, root) {
      var items = visibleFocusables(root);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* --------------------------------------------------------------- search */
    _initSearch() {
      var self = this;
      this.search = this.querySelector('#pa-search-overlay');
      this.searchOpen = false;
      if (!this.search) return;
      this.searchInput = this.search.querySelector('[data-pa-search-input]');
      this.searchBtns = Array.prototype.slice.call(this.querySelectorAll('[data-pa-search-open]'));
      this.calCol = this.search.querySelector('[data-pa-col-cal]');
      this.calOut = this.search.querySelector('[data-pa-cal-out]');
      this.prodCol = this.search.querySelector('[data-pa-col-prod]');
      this.prodOut = this.search.querySelector('[data-pa-products]');
      this.deals = Array.prototype.slice.call(this.search.querySelectorAll('[data-pa-deal]'));
      var src = this.search.querySelector('template[data-pa-cal-src]');
      this.calSrc = [];
      if (src && src.content) {
        Array.prototype.forEach.call(src.content.querySelectorAll('li[data-keys]'), function (li) {
          var keys = (li.getAttribute('data-keys') || '').split(',').map(function (k) {
            return k.trim().toLowerCase();
          }).filter(Boolean);
          self.calSrc.push({keys: keys, q: li.getAttribute('data-q') || '', block: li.getAttribute('data-block') || '', node: li});
        });
      }
      this.searchUrl = (this.getAttribute('data-search-url') || '/search/suggest').replace(/\/$/, '');

      this.searchBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self._searchReturn = btn;
          if (self.searchOpen) self.closeSearch(true);
          else self.openSearch();
        });
      });
      var close = this.search.querySelector('[data-pa-search-close]');
      if (close) close.addEventListener('click', function () {
        self.closeSearch(true);
      });
      if (this.searchInput) {
        this.searchInput.addEventListener('input', function () {
          clearTimeout(self._searchTimer);
          self._searchTimer = setTimeout(function () {
            self._query(self.searchInput.value);
          }, 180);
        });
      }
      this.search.addEventListener('keydown', function (event) {
        if (event.key === 'Tab') self._trap(event, self.search);
      });
      // Re-measure result meta lines when the sheet changes width (rotation, split view).
      window.addEventListener('resize', function () {
        if (!self.searchOpen || self._fitRaf) return;
        self._fitRaf = window.requestAnimationFrame(function () {
          self._fitRaf = null;
          self._fitMeta();
        });
      }, {passive: true});
    }

    openSearch() {
      if (!this.search || this.searchOpen) return;
      this.closeMenu(false);
      if (this.drawerOpen) this.closeDrawer(false);
      this.searchOpen = true;
      this.searchBtns.forEach(function (b) {
        b.setAttribute('aria-expanded', 'true');
      });
      reveal(this.search);
      this._sync();
      if (isDesktop()) {
        this._edgeScrim(this.search);
        reveal(this.scrim);
      } else {
        this._searchLocked = true;
        lockScroll(true);
      }
      var input = this.searchInput;
      if (input) {
        input.focus({preventScroll: true});
        this._query(input.value);
      }
    }

    closeSearch(returnFocus) {
      if (!this.search || !this.searchOpen) return;
      this.searchOpen = false;
      this.searchBtns.forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
      });
      conceal(this.search, SHEET_MS);
      this._sync();
      if (!this.openName) conceal(this.scrim, PANEL_MS);
      if (this._searchLocked) {
        this._searchLocked = false;
        lockScroll(false);
      }
      if (returnFocus) {
        var back = this._searchReturn && this._searchReturn.offsetParent ? this._searchReturn : this.searchBtns.filter(function (b) {
          return b.offsetParent;
        })[0];
        if (back) back.focus({preventScroll: true});
      }
    }

    _norm(q) {
      return String(q || '').toLowerCase().replace(/\s+/g, ' ').trim();
    }

    _matches(keys, q) {
      if (!q) return false;
      var tokens = q.split(' ');
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i];
        if (k === q || k.indexOf(q) === 0 || q.indexOf(k + ' ') === 0) return true;
        for (var j = 0; j < tokens.length; j++) {
          var t = tokens[j];
          if (!t) continue;
          if (t === k || (t.length >= 2 && k.indexOf(t) === 0) || (t.length === 1 && k === t)) return true;
        }
      }
      return false;
    }

    _query(raw) {
      var self = this;
      var q = this._norm(raw);
      if (this._ctrl) this._ctrl.abort();
      if (!q) {
        this.search.setAttribute('data-state', 'idle');
        if (this.calCol) this.calCol.hidden = true;
        if (this.prodCol) this.prodCol.hidden = true;
        this._pickDeal('');
        return;
      }
      this.search.setAttribute('data-state', 'live');

      // 1. Calibers: the shorthand map (each caliber block's keys).
      var hits = this.calSrc.filter(function (c) {
        return self._matches(c.keys, q);
      });
      if (this.calOut) {
        this.calOut.textContent = '';
        hits.forEach(function (c) {
          self.calOut.appendChild(c.node.cloneNode(true));
        });
      }
      if (this.calCol) this.calCol.hidden = hits.length === 0;
      this.search.setAttribute('data-cal', String(hits.length));

      // 3. Deal row.
      this._pickDeal(q);

      // 2. Products from the predictive endpoint, rendered by pa-predictive-search.
      if (!this.prodCol || !this.prodOut) return;
      this.prodCol.hidden = false;
      this.prodOut.setAttribute('aria-busy', 'true');
      var ctrl = window.AbortController ? new AbortController() : null;
      this._ctrl = ctrl;
      // Shorthand typed on its own ("556", "9", "22lr") searches the canonical caliber name,
      // because product titles say "5.56", not "556".
      var terms = raw.trim();
      var exact = null;
      for (var h = 0; h < hits.length; h++) {
        if (hits[h].q && hits[h].keys.indexOf(q) !== -1) {
          terms = hits[h].q;
          exact = hits[h];
          break;
        }
      }
      // Exact caliber shorthand: the header already rendered that caliber's rows. Instant.
      if (exact && exact.block) {
        var tpl = this.search.querySelector('template[data-pa-cal-products="' + exact.block + '"]');
        if (tpl && tpl.content && tpl.content.querySelector('.pa-sr__row')) {
          this.prodOut.textContent = '';
          this.prodOut.appendChild(document.importNode(tpl.content, true));
          this.prodOut.setAttribute('aria-busy', 'false');
          this._fitMeta();
          return;
        }
      }
      // Anything else: hold the column with quiet placeholder rows while the endpoint answers.
      if (!this.prodOut.querySelector('.pa-sr__row')) {
        this.prodOut.textContent = '';
        var ghosts = document.createElement('div');
        ghosts.className = 'pa-sr__ghosts';
        ghosts.setAttribute('aria-hidden', 'true');
        for (var g = 0; g < 3; g++) {
          var row = document.createElement('div');
          row.className = 'pa-sr__ghost';
          ghosts.appendChild(row);
        }
        this.prodOut.appendChild(ghosts);
      }
      var url = this.searchUrl + '?q=' + encodeURIComponent(terms) +
        '&section_id=pa-predictive-search' +
        '&resources%5Btype%5D=product&resources%5Blimit%5D=10' +
        '&resources%5Boptions%5D%5Bunavailable_products%5D=last' +
        '&resources%5Boptions%5D%5Bfields%5D=title,product_type,variants.title,vendor,tag';
      var attempt = function (n) {
        return fetch(url, {credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined, headers: {Accept: 'text/html'}})
          .then(function (r) {
            // Rate limits and gateway hiccups: three short retries, then fall back to a link.
            if ((r.status === 429 || r.status >= 500) && n < 3) {
              return new Promise(function (res) {
                setTimeout(res, 1500 * (n + 1));
              }).then(function () {
                return attempt(n + 1);
              });
            }
            if (!r.ok) throw new Error('search ' + r.status);
            return r.text();
          });
      };
      attempt(0)
        .then(function (html) {
          if (ctrl && ctrl.signal.aborted) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var sr = doc.querySelector('[data-pa-sr]');
          self.prodOut.textContent = '';
          if (sr) self.prodOut.appendChild(document.importNode(sr, true));
          self.prodOut.setAttribute('aria-busy', 'false');
          self._fitMeta();
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          // Endpoint unavailable: leave a working way through to the full results page.
          var form = self.search.querySelector('form');
          var a = document.createElement('a');
          a.className = 'pa-cta-link pa-sr__all';
          a.href = (form ? form.getAttribute('action') : '/search') + '?q=' + encodeURIComponent(raw.trim()) + '&type=product&options%5Bprefix%5D=last';
          var label = document.createElement('span');
          label.textContent = 'See all results for \u201C' + raw.trim() + '\u201D';
          a.appendChild(label);
          var arrow = self.search.querySelector('.pa-sdeal__cta svg');
          if (arrow) a.appendChild(arrow.cloneNode(true));
          self.prodOut.textContent = '';
          self.prodOut.appendChild(a);
          self.prodOut.setAttribute('aria-busy', 'false');
        });
    }

    /* Result rows: `{price} · {pack} · ■ IN STOCK` may wrap on narrow phones. Mark the segment
       that ends each line so its trailing separator is dropped (pa-header.css). */
    _fitMeta() {
      if (!this.prodOut) return;
      Array.prototype.forEach.call(this.prodOut.querySelectorAll('.pa-sr__meta'), function (meta) {
        var parts = Array.prototype.filter.call(meta.children, function (el) {
          return el.offsetParent !== null;
        });
        parts.forEach(function (el) {
          el.classList.remove('is-line-end');
        });
        for (var i = 0; i < parts.length - 1; i++) {
          if (parts[i + 1].offsetTop > parts[i].offsetTop + 2) parts[i].classList.add('is-line-end');
        }
      });
    }

    _pickDeal(q) {
      if (!this.deals.length) return;
      var self = this;
      var phone = window.matchMedia('(max-width: 749px)').matches;
      // Before typing on the sheet, list the first deals: two 96 px rows on a phone (below the
      // 3 x 2 chip grid), four on a tablet (no empty half-screen).
      if (!q && !isDesktop()) {
        var max = phone ? 2 : 4;
        this.deals.forEach(function (d, i) {
          d.hidden = i >= max;
        });
        return;
      }
      // Live: the deals whose keys match the terms (two on a phone, where the column runs under
      // a short product list, and two in the desktop right column (r2 H3); one on the tablet
      // sheet), else the first deal.
      var picks = [];
      if (q) {
        this.deals.forEach(function (d) {
          var keys = (d.getAttribute('data-keys') || '').split(',').map(function (k) {
            return k.trim();
          }).filter(Boolean);
          if (self._matches(keys, q)) picks.push(d);
        });
      }
      if (!q && isDesktop()) {
        // Idle desktop (r2 H3): the first two deals, the same pair the phone lists.
        picks = this.deals.slice(0, 2);
      }
      if (!picks.length) picks.push(this.deals[0]);
      if (picks.length < 2 && isDesktop() && this.deals.length > 1) {
        // Desktop always shows two: top up with the next deal in order.
        for (var j = 0; j < this.deals.length && picks.length < 2; j++) {
          if (picks.indexOf(this.deals[j]) === -1) picks.push(this.deals[j]);
        }
      }
      picks = picks.slice(0, phone || isDesktop() ? 2 : 1);
      this.deals.forEach(function (d) {
        d.hidden = picks.indexOf(d) === -1;
      });
    }

    /* ----------------------------------------------------------- global keys */
    _onKey(event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (this.searchOpen) {
        event.preventDefault();
        this.closeSearch(true);
      } else if (this.drawerOpen) {
        event.preventDefault();
        this.closeDrawer(true);
      } else if (this.openName) {
        event.preventDefault();
        this.closeMenu(true);
      }
    }

    _onDocClick(event) {
      if (this.openName) {
        var t = event.target;
        var panel = this._panel(this.openName);
        var trigger = this._trigger(this.openName);
        if (!(panel && panel.contains(t)) && !(trigger && trigger.contains(t))) {
          this.closeMenu(false);
        }
      }
      if (this.searchOpen && isDesktop() && !this.contains(event.target)) {
        this.closeSearch(false);
      }
    }

    _onFocusIn(event) {
      if (this.openName && !this.contains(event.target)) this.closeMenu(false);
      if (this.openName) {
        var panel = this._panel(this.openName);
        var trigger = this._trigger(this.openName);
        var inside = (panel && panel.contains(event.target)) || (trigger && trigger.contains(event.target));
        if (!inside && this.contains(event.target)) this.closeMenu(false);
      }
      if (this.searchOpen && !this.search.contains(event.target) && !this.searchBtns.some(function (b) {
        return b === event.target;
      })) {
        if (!this.contains(event.target) || isDesktop()) this.closeSearch(false);
      }
    }

    /* ----------------------------------------------------------------- cart */
    _initCart() {
      var self = this;
      this.cartLink = document.getElementById('cart-icon-bubble');
      if (!this.cartLink) return;
      this._bubbleHtml = this.cartLink.innerHTML;
      this._refresh = function () {
        clearTimeout(self._cartTimer);
        self._cartTimer = setTimeout(function () {
          self.refreshCount();
        }, 250);
      };
      ['ajaxProduct:added', 'cart:refresh', 'cart:updated', 'cart:change', 'upcart:cart:updated'].forEach(function (name) {
        document.addEventListener(name, self._refresh);
      });
      window.addEventListener('pageshow', function (event) {
        if (event.persisted) self._refresh();
      });
      // Cart writes from any script (UpCart, global.js, product-form.js): watch the
      // resource timeline instead of wrapping fetch (the funnels own window.fetch).
      if ('PerformanceObserver' in window) {
        try {
          this._perf = new PerformanceObserver(function (list) {
            var hit = list.getEntries().some(function (e) {
              return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(e.name);
            });
            if (hit) self._refresh();
          });
          this._perf.observe({type: 'resource', buffered: false});
        } catch (e) {
          this._perf = null;
        }
      }
      // Legacy cart scripts replace the bubble's innerHTML with the legacy markup:
      // restore the brand markup and re-read the count.
      if ('MutationObserver' in window) {
        this._bubbleObs = new MutationObserver(function () {
          if (!self.cartLink.querySelector('[data-pa-cart-count]')) {
            self._bubbleObs.disconnect();
            self.cartLink.innerHTML = self._bubbleHtml;
            self._bubbleObs.observe(self.cartLink, {childList: true});
            self._refresh();
          }
        });
        this._bubbleObs.observe(this.cartLink, {childList: true});
      }
    }

    refreshCount(attempt) {
      var self = this;
      var n = attempt || 0;
      var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var doFetch = window.__nativeFetch || window.fetch;
      doFetch.call(window, base + 'cart.js', {credentials: 'same-origin', headers: {Accept: 'application/json'}})
        .then(function (r) {
          if (!r.ok) throw new Error('cart ' + r.status);
          return r.json();
        })
        .then(function (cart) {
          self.setCount(cart && typeof cart.item_count === 'number' ? cart.item_count : 0);
        })
        .catch(function () {
          // A busy storefront (429) should not leave a stale count: two spaced retries.
          if (n < 2) setTimeout(function () {
            self.refreshCount(n + 1);
          }, 1500 * (n + 1));
        });
    }

    setCount(n) {
      if (!this.cartLink) return;
      var bubble = this.cartLink.querySelector('[data-pa-cart-count]');
      if (bubble) {
        bubble.textContent = n > 99 ? '99+' : String(n);
        bubble.hidden = n === 0;
      }
      this.cartLink.setAttribute('aria-label', 'Cart, ' + n + (n === 1 ? ' item' : ' items'));
    }
  }

  customElements.define('pa-header', PaHeader);
})();
